import type { SpanAttributeDocument } from '@app/modules/monitor/models/span-attributes.model';
import type { ScoreRollup } from '@app/modules/monitor/models/trace-score.model';
import {
    MODULE_ROUTE_MAP,
    MODULES,
    PAGE_ROUTE_MAP,
    PAGES,
} from '@app/shared/constants/shared.constant';

/**
 * Types for `GET /api/v1/traces*`.
 *
 * Every timestamp here is a naive-UTC string with no `Z`; read it with `parseNaiveUtc`, never with
 * `new Date()`. Every `costStatus` is an open set of codes, not a union; see `costStatusLabel`.
 */

import type { DimensionFacet } from '@app/modules/monitor/models/trace-explore.model';

export type TraceStatus = 'unset' | 'ok' | 'error';

export type TraceSort = 'started_at_desc' | 'started_at_asc' | 'cost_desc' | 'tokens_desc';

/** Whether the platform's own AI traces are in the answer. The API defaults to `exclude`. */
export type PlatformFilter = 'exclude' | 'only' | 'include';

/** Span kinds from `ggx_otel.SpanKind`, which mirrors OpenInference. `kind` may hold others. */
export type SpanKind =
    | 'llm'
    | 'embedding'
    | 'retriever'
    | 'reranker'
    | 'tool'
    | 'agent'
    | 'chain'
    | 'guardrail'
    | 'evaluator'
    | 'other';

export interface TraceItem {
    traceId: string;
    name: string;
    projectId: number | null;
    deploymentId: number | null;
    pipelineVersionId: number | null;
    status: TraceStatus;
    /** Naive UTC, no `Z`. */
    startedAt: string;
    /** Naive UTC, no `Z`. */
    endedAt: string;
    durationMs: number;
    serviceName: string | null;
    environment: string | null;
    sessionId: string | null;
    userId: string | null;
    tags: string[] | null;
    /**
     * The first characters of the root span's input and output. Cut short on a list row (240
     * characters) and carried at the header's own length (2000) on the detail.
     */
    inputPreview: string | null;
    outputPreview: string | null;
    /**
     * `provider/model` per metered span, and the span kinds present, each distinct and in order
     * of first use. Empty on a header written before the API recorded them.
     */
    models: string[] | null;
    spanKinds: string[] | null;
    /** One rollup per score name. Filled on the list; empty elsewhere. */
    scores: ScoreRollup[];
    spanCount: number;
    errorCount: number;
    inputTokens: number;
    outputTokens: number;
    costMicros: number | null;
    currency: string | null;
    /** `priced`, `partial`, `unpriced`, `mixed_currency`, `no_metered_spans`, or a newer code. */
    costStatus: string;
    /** The detail behind the code, when it has one. Null when there is a total. */
    costReason: string | null;
    unpricedSpanCount: number;
    /** Spans whose content the project's masking policy replaced or dropped at ingest. */
    maskedSpanCount: number;
    /** LLM or embedding spans that named a model but reported no token usage. */
    zeroTokenSpanCount: number;
    /** LLM or embedding spans that named no model at all. */
    noModelSpanCount: number;
    /**
     * The trace's trajectory. Tool and agent spans; time in tool and LLM spans (summed, so parallel
     * calls can add up to more than the trace's own duration); tool calls repeating an earlier call's
     * tool and arguments; the longest run of one tool under one parent; answers that were empty; and
     * the maintenance pass's cost verdict. Zero on a trace stored before these existed.
     */
    toolCallCount: number;
    agentCount: number;
    toolMs: number;
    llmMs: number;
    duplicateToolCallCount: number;
    maxToolRepeat: number;
    emptyOutputCount: number;
    costOutlier: boolean;
    /** `duplicate_tools`, `loop`, `cost_outlier`, `empty_output`, or a newer code. */
    flags: string[];
    /**
     * True when this trace is the platform's own AI spend — the assistant, prompt analysis,
     * LLM-as-judge — rather than yours. Priced from the same sheet, which is exactly why the list
     * leaves these out unless asked: an unlabelled platform row in a cost total reads as your bill.
     */
    platform: boolean;
}

export interface SpanSummary {
    spanId: string;
    parentSpanId: string | null;
    name: string;
    kind: SpanKind | string;
    /** Naive UTC, no `Z`. */
    startTime: string;
    /** Naive UTC, no `Z`. */
    endTime: string;
    durationMs: number;
    status: string;
    statusMessage: string | null;
    provider: string | null;
    requestModel: string | null;
    responseModel: string | null;
    inputTokens: number | null;
    outputTokens: number | null;
    costMicros: number | null;
    currency: string | null;
    costStatus: string;
    /** True when the input, output or attributes of this span were cut at ingest. */
    truncated: boolean;
    /** Milliseconds from the span's start to the first streamed token, when instrumentation said. */
    ttftMs: number | null;
    /** Instrumentation health flags: `zero_tokens`, `no_model`, or a newer code. */
    health: string[];
    /**
     * Agent structure as the instrumentation named it (`agent.name`, `tool.name`, OpenInference's
     * `graph.node.id` and `graph.node.parent_id`). Absent or null on older spans. What the Graph
     * view is built from; see `buildAgentGraph`.
     */
    agentName?: string | null;
    toolName?: string | null;
    graphNodeId?: string | null;
    graphNodeParentId?: string | null;
}

/** One span event as stored, already masked. `exception` events carry why a span failed. */
export interface SpanEvent {
    name: string;
    /** Naive UTC, no `Z`. */
    time: string;
    attributes: Record<string, unknown>;
}

/** An `exception` event, read into the fields the Errors section shows. */
export interface SpanError {
    type: string;
    message: string | null;
    stacktrace: string | null;
    time: string;
}

export interface SpanDetail extends SpanSummary {
    convention: string;
    serviceName: string | null;
    environment: string | null;
    sessionId: string | null;
    userId: string | null;
    tags: string[];
    input: string | null;
    output: string | null;
    attributes: Record<string, unknown>;
    resourceAttributes: Record<string, unknown>;
    /** `{count, keys}` naming what the per-span cap dropped, or null when nothing was dropped. */
    droppedAttributes: { count: number; keys: string[] } | null;
    events: SpanEvent[];
    /**
     * Every stored attribute of the span as one nested, OpenInference-shaped document, with its
     * scores and `lifted_from`. Keys are the API's own data keys (snake_case), not camelCased.
     * Absent from a server older than the document.
     */
    allAttributes?: SpanAttributeDocument;
}

/**
 * The exception events of a span, as type, message and stack trace, in the order they happened.
 * @param events The span's events, which may be absent on an older response.
 * @returns One entry per `exception` event; other events are left out.
 */
export function spanErrors(events: SpanEvent[] | null | undefined): SpanError[] {
    const text = (value: unknown): string | null =>
        typeof value === 'string' && value ? value : null;

    return (events ?? [])
        .filter(event => event.name === 'exception')
        .map(event => ({
            type: text(event.attributes['exception.type']) ?? 'Exception',
            message: text(event.attributes['exception.message']),
            stacktrace: text(event.attributes['exception.stacktrace']),
            time: event.time,
        }));
}

const HEALTH_LABELS: Record<string, string> = {
    zero_tokens: 'No token usage',
    no_model: 'No model',
};

/**
 * A health flag in words.
 * @param flag The flag as the API sends it.
 * @returns Its label, or the flag itself when this build does not know it yet.
 */
export function healthLabel(flag: string): string {
    return HEALTH_LABELS[flag] ?? flag;
}

/**
 * What made a trace, named and linkable.
 *
 * The header stores ids only, and an id on its own is not something a reader can follow. The
 * pipeline is resolved from the version to the pipeline it is a version of, because that is what
 * has a page. A name left null means the object is gone or unreadable; the id still shows.
 */
/**
 * One asset the pipeline behind a trace declares as an input.
 *
 * Read from the pipeline, not from the spans: a retrieval happens inside exec'd user code, so no
 * span carries a RAG id, but the version that ran names every asset it was built from.
 */
export interface TraceAsset {
    kind: 'rag' | 'prompt' | 'foundation_model';
    /** The asset's own id, for grouping. Not what its page is keyed by: see `versionId`. */
    id: number;
    /** The declared version's id, which the asset's registry page is keyed by. */
    versionId: number;
    name: string;
    version: number;
}

export interface TraceOrigin {
    deploymentId: number | null;
    deploymentName: string | null;
    pipelineId: number | null;
    pipelineName: string | null;
    pipelineVersion: number | null;
    pipelineVersionId: number | null;
    assets: TraceAsset[];
}

export interface TraceDetail extends TraceItem {
    origin: TraceOrigin;
    /** One page of the span tree, ordered by `(startTime, spanId)`. */
    spans: SpanSummary[];
    /**
     * True when more spans follow this page. `spanCount` in the header is the real total, and is
     * not affected by the per-response cap.
     *
     * Declared because the API sends it, but nothing here reads it: `nextSpanId` says the same
     * thing and is the value the client actually needs, so following one field rather than two
     * removes the chance of them disagreeing.
     */
    spansTruncated: boolean;
    /**
     * The cursor for the next page: pass it back as `afterSpanId`. Null when this is the last page.
     *
     * Paging is by a span's place in the trace rather than by an offset, so a span redelivered by
     * the exporter between two requests can neither repeat nor hide another one.
     */
    nextSpanId: string | null;
}

export interface FacetValue {
    value: string;
    count: number;
}

export interface FacetValues {
    values: FacetValue[];
    /** True when more values existed than were returned. The selected value is always included. */
    truncated: boolean;
}

export interface TraceFacets {
    environment: FacetValues;
    name: FacetValues;
    status: FacetValues;
    /** One per configured metadata dimension of the filtered project; absent from older APIs. */
    dimensions?: DimensionFacet[];
}

export interface UnpricedModel {
    provider: string | null;
    model: string | null;
    /** How many spans of this model the scan saw unpriced. A lower bound when the scan was cut. */
    spanCount: number;
}

/**
 * `GET /traces/unpriced-models`: which models have no price, and how sure of it the API is.
 *
 * The scan walks spans up to a 500 000-row budget and stops there. When it stops, `truncated` is
 * true and the answer is a sample, not a census: every `spanCount` is a lower bound, and a model
 * whose spans all sit past the budget is absent from `models` entirely. Nothing built from this
 * may read as exhaustive while `truncated` is true.
 */
export interface UnpricedModels {
    models: UnpricedModel[];
    /** True when the scan hit its row budget before reaching the end. */
    truncated: boolean;
    /** How many rows the scan actually walked. */
    scannedRows: number;
}

/** How many model names the banner names before it counts the rest. */
const UNPRICED_NAMES_SHOWN = 3;

/**
 * The unpriced-models banner, in words, or null when there is nothing to say.
 *
 * A truncated scan is stated twice over — "at least" in front of the count, and the incompleteness
 * of the list itself — because they are two different shortfalls and a reader acting on this is
 * deciding which prices to go and add. Understating it sends them away thinking they are done.
 * @param report The API's answer, or null when it could not be loaded.
 * @returns The sentence for the banner, or null.
 */
export function unpricedModelsNote(report: UnpricedModels | null | undefined): string | null {
    const models = report?.models ?? [];
    if (models.length === 0) return null;

    const names = models
        .slice(0, UNPRICED_NAMES_SHOWN)
        .map(model => `${model.provider ?? 'unknown'}/${model.model ?? 'unknown'}`)
        .join(', ');
    const listed =
        models.length > UNPRICED_NAMES_SHOWN
            ? `${names} and ${models.length - UNPRICED_NAMES_SHOWN} more`
            : names;
    const spans = models.reduce((total, model) => total + (model.spanCount ?? 0), 0);
    const counted = `${spans.toLocaleString('en-US')} span(s)`;

    if (!report?.truncated) {
        return `${counted} from ${listed} have no price, so their cost is missing from every total here.`;
    }

    return (
        `At least ${counted} from ${listed} have no price, so their cost is missing from every ` +
        `total here. The scan stopped after ${(report.scannedRows ?? 0).toLocaleString('en-US')} ` +
        'rows, so these counts are lower bounds and other models may be missing from this list.'
    );
}

/**
 * One currency's total across the filtered window.
 *
 * `GET /traces/summary` returns a list of these and deliberately no single total: micros in
 * different currencies do not add up, and a trace whose own spans collided (`mixed_currency`) has
 * no total to contribute to any of them. Never sum these in the client.
 */
export interface CurrencyTotal {
    currency: string;
    costMicros: number;
    traceCount: number;
}

/**
 * How many traces are in each cost state, using the same names as a trace's own `costStatus`.
 *
 * This is a fixed set of counters, unlike `costStatus` itself: the API declares exactly these five
 * fields, so a code added on the ingest side would not appear here.
 */
export interface CostStatusCounts {
    priced: number;
    partial: number;
    unpriced: number;
    mixedCurrency: number;
    noMeteredSpans: number;
}

/** `GET /traces/summary`: the KPI strip, over the same filters as the list. */
export interface TraceSummary {
    traceCount: number;
    errorTraceCount: number;
    errorRate: number;
    /** Trace latency by nearest rank. Null when the window holds no traces at all. */
    p50DurationMs: number | null;
    p90DurationMs: number | null;
    p95DurationMs: number | null;
    p99DurationMs: number | null;
    unpricedSpanCount: number;
    maskedSpanCount: number;
    zeroTokenSpanCount: number;
    noModelSpanCount: number;
    costByCurrency: CurrencyTotal[];
    costStatusCounts: CostStatusCounts;
    /**
     * Traces that could have cost something and have no total at all.
     *
     * They are counted in `traceCount` and in no entry of `costByCurrency`, so this is the number
     * that says the cost tiles are not the whole window. Never present the tiles as complete while
     * this is above zero.
     */
    tracesWithoutTotal: number;
    /** True when the percentile came from the most recent traces rather than from all of them. */
    latencySampled: boolean;
    /** Traces carrying each trajectory flag. */
    flagCounts: FlagCounts;
    /** Time in tool and LLM spans, summed over the window. */
    toolMs: number;
    llmMs: number;
    /**
     * False when a `span(...)` term of the query was cut at its cap or scan budget, which makes
     * these numbers and the list beside them a lower bound. Absent from APIs before tracing 15.
     */
    spanMatchComplete?: boolean;
}

export interface FlagCounts {
    duplicateTools: number;
    loop: number;
    costOutlier: number;
    emptyOutput: number;
}

/** A trajectory flag, as the API names it and the `flag` filter takes it. */
export type TraceFlag = 'duplicate_tools' | 'loop' | 'cost_outlier' | 'empty_output';

/** The flags the filter offers, in the order a trace lists them. */
export const TRACE_FLAGS: { value: TraceFlag; label: string; description: string }[] = [
    {
        value: 'duplicate_tools',
        label: 'Repeated tool calls',
        description: 'A tool was called again with the same arguments.',
    },
    {
        value: 'loop',
        label: 'Same call in a row',
        description: 'The same tool was called with the same arguments several times in a row.',
    },
    {
        value: 'cost_outlier',
        label: 'Cost outliers',
        description: 'Cost far more than the median trace of the same name.',
    },
    {
        value: 'empty_output',
        label: 'Empty outputs',
        description: 'The trace or one of its model calls recorded a blank answer.',
    },
];

/**
 * The flags a URL's `flags` parameter selects. The URL holds them as one comma list, so a filtered
 * list stays one readable link; the API takes them as a repeated `flag`.
 * @param raw The parameter, or null when it is absent.
 * @returns The known flags among them, once each, in filter order.
 */
export function parseFlagParam(raw: string | null | undefined): TraceFlag[] {
    const asked = new Set((raw ?? '').split(','));

    return TRACE_FLAGS.map(flag => flag.value).filter(flag => asked.has(flag));
}

/**
 * The `flags` parameter with one flag turned on or off.
 * @param raw The parameter as it is.
 * @param flag The flag that was clicked.
 * @param checked Whether it is now on.
 * @returns The new parameter, or null to clear it.
 */
export function toggleFlagParam(
    raw: string | null | undefined,
    flag: TraceFlag,
    checked: boolean,
): string | null {
    const current = parseFlagParam(raw);
    const next = checked
        ? [...current.filter(item => item !== flag), flag]
        : current.filter(item => item !== flag);

    return next.length > 0 ? next.join(',') : null;
}

/** One badge in the list or the detail header. */
export interface FlagBadge {
    flag: string;
    text: string;
    tooltip: string;
}

/**
 * A trace's flags as badges.
 * @param item The flags and the numbers behind them. Any of them may be absent on an older response.
 * @returns One badge per flag, in the order the trace lists them.
 */
export function flagBadges(
    item: Partial<
        Pick<TraceItem, 'flags' | 'duplicateToolCallCount' | 'maxToolRepeat' | 'emptyOutputCount'>
    >,
): FlagBadge[] {
    const duplicates = item.duplicateToolCallCount ?? 0;
    const repeat = item.maxToolRepeat ?? 0;
    const empty = item.emptyOutputCount ?? 0;
    const badge = (flag: string): FlagBadge => {
        switch (flag) {
            case 'duplicate_tools':
                return {
                    flag,
                    text: `Repeated tool ×${duplicates}`,
                    tooltip: `${duplicates} tool ${duplicates === 1 ? 'call' : 'calls'} repeated an earlier call with the same arguments.`,
                };
            case 'loop':
                return {
                    flag,
                    text: `Same call ×${repeat} in a row`,
                    tooltip: `One tool was called with the same arguments, ${repeat} times in a row under the same parent span, which is what a stuck retry loop looks like.`,
                };
            case 'cost_outlier':
                return {
                    flag,
                    text: 'Cost outlier',
                    tooltip:
                        'Cost more than the configured multiple (10× by default) of the median trace with the same name over the last 7 days.',
                };
            case 'empty_output':
                return {
                    flag,
                    text: 'Empty output',
                    tooltip: `${empty} ${empty === 1 ? 'answer was' : 'answers were'} blank: the trace's own output, or a model call that was asked something, recorded an output, and said nothing in it.`,
                };
            default:
                return { flag, text: flag, tooltip: flag };
        }
    };

    return (item.flags ?? []).map(badge);
}

/** How a trace's busy time split between tools and the model. */
export interface TimeSplit {
    toolMs: number;
    llmMs: number;
    toolPercent: number;
    llmPercent: number;
}

/**
 * Tool time against model time, as shares of their sum (not of the trace's duration: parallel
 * calls each count in full).
 * @param toolMs Time in tool spans.
 * @param llmMs Time in LLM spans.
 * @returns The split, or null when neither has any time.
 */
export function timeSplit(
    toolMs: number | null | undefined,
    llmMs: number | null | undefined,
): TimeSplit | null {
    const tool = toolMs ?? 0;
    const llm = llmMs ?? 0;
    if (tool + llm <= 0) return null;
    const toolPercent = Math.round((tool / (tool + llm)) * 100);

    return { toolMs: tool, llmMs: llm, toolPercent, llmPercent: 100 - toolPercent };
}

/**
 * The KPI strip's line about flagged traces.
 * @param summary The window totals; `flagCounts` is absent on an older API.
 * @returns The line, or null when nothing in the window is flagged.
 */
export function flagCountsNote(
    summary: Partial<Pick<TraceSummary, 'flagCounts'>> | null | undefined,
): string | null {
    const counts = summary?.flagCounts;
    if (!counts) return null;
    const parts = [
        counts.duplicateTools > 0
            ? `${counts.duplicateTools.toLocaleString('en-US')} with repeated tool calls`
            : null,
        counts.loop > 0 ? `${counts.loop.toLocaleString('en-US')} in a loop` : null,
        counts.costOutlier > 0
            ? `${counts.costOutlier.toLocaleString('en-US')} cost outliers`
            : null,
        counts.emptyOutput > 0
            ? `${counts.emptyOutput.toLocaleString('en-US')} with an empty output`
            : null,
    ].filter((part): part is string => part !== null);

    return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Every filter the list, facets and summary endpoints accept. Nulls are dropped by `RestService`.
 *
 * The window comes in two forms and the API takes the datetime pair over the day pair whenever
 * either half of it is set, so exactly one form is ever populated here. `startTime`/`endTime` are
 * half-open — start inclusive, end exclusive — and are sent as ISO strings with an explicit `Z`.
 * `startDate`/`endDate` are whole UTC days and both inclusive.
 */
export interface TraceFilterParams {
    startDate: string | null;
    endDate: string | null;
    startTime: string | null;
    endTime: string | null;
    projectId: number | null;
    environment: string | null;
    name: string | null;
    status: TraceStatus | null;
    sessionId: string | null;
    userId: string | null;
    search: string | null;
    /** Omitted or null means the API's default, `exclude`. */
    platform?: PlatformFilter | null;
    /** Traces with a score of this name; the three below narrow it and need it. */
    scoreName?: string | null;
    scoreMin?: number | null;
    scoreMax?: number | null;
    scoreLabel?: string | null;
    /** Traces carrying any of these flags. Sent as a repeated `flag` parameter. */
    flag?: TraceFlag[] | null;
    /** A filter-language query over the traces; see `GET /traces/query-fields`. */
    query?: string | null;
    /** Metadata dimensions, each `key:value`. Sent as a repeated `dim` parameter. */
    dim?: string[] | null;
}

/**
 * How many spans the detail page will follow the cursor for before it stops asking.
 *
 * The API pages the tree and keeps handing out a `nextSpanId` for as long as there is more, so
 * something has to decide when enough of a very large trace is on screen. Stopping is always said
 * in words above the tree; see `nextSpanCursor`.
 */
export const MAX_SPANS_FETCHED = 20_000;

/**
 * The most spans this view will ever hold, however many times the reader asks for more.
 *
 * Five batches of {@link MAX_SPANS_FETCHED}. The number is the same 100 000 the trace list already
 * refuses to page past, so the two screens stop at one ceiling rather than two, and it is a size a
 * virtual scroller and the `Map` built over it stay comfortable at. Without a ceiling the budget
 * rose by a batch per click with nothing to stop it, which is not a limit at all — it only moved
 * the failure somewhere harder to see.
 *
 * A trace larger than this is a query, not a page: the API serves the whole tree to a caller that
 * wants it. Reaching this is stated on screen, not silently done.
 */
export const MAX_SPAN_BUDGET = 5 * MAX_SPANS_FETCHED;

/**
 * The budget after an explicit "load more", never above {@link MAX_SPAN_BUDGET}.
 *
 * Raising it by a batch is what makes the next click fetch a batch rather than a single page: the
 * test that stopped the chain has to stop being true. `held` is folded in because a chain can end
 * holding more than the budget it started with.
 * @param budget The budget in force now.
 * @param held How many spans are held.
 * @param batch How much one continuation adds.
 * @param ceiling The most this view will ever hold.
 * @returns The new budget, which never exceeds `ceiling`.
 */
export function raiseSpanBudget(
    budget: number,
    held: number,
    batch: number = MAX_SPANS_FETCHED,
    ceiling: number = MAX_SPAN_BUDGET,
): number {
    return Math.min(Math.max(budget, held) + batch, ceiling);
}

/**
 * The cursor for the next page of a span tree, or null when the client should stop.
 *
 * Two things end the loop: the API saying this was the last page (`nextSpanId` is null), and the
 * client having taken as much as it will render. Separated from the component so the termination
 * is testable without a server — an unbounded follow of a server-supplied cursor is exactly the
 * kind of loop that should not be proven only by running it.
 * @param detail The page just received.
 * @param heldSpanCount How many spans are held after merging that page in.
 * @param budget How many spans this view will hold before stopping. Raised by an explicit
 *   continuation, so asking for more actually fetches more instead of stopping on the same test.
 * @returns The value to send as `afterSpanId`, or null to stop.
 */
export function nextSpanCursor(
    detail: TraceDetail,
    heldSpanCount: number,
    budget: number = MAX_SPANS_FETCHED,
): string | null {
    if (!detail.nextSpanId) return null;
    if (heldSpanCount >= budget) return null;

    return detail.nextSpanId;
}

/**
 * What the KPI strip's cost tiles leave out, in words, or null when they leave out nothing.
 *
 * `tracesWithoutTotal` counts the traces that could have cost something and have no total at all:
 * nothing priced them, or their own spans priced in several currencies and so cannot be added up.
 * They are counted in `traceCount` and in no entry of `costByCurrency`, so without this sentence
 * the tiles read as the cost of the whole window when they are not.
 *
 * The reasons are named only when they account for some of the shortfall. `costStatusCounts` is a
 * fixed set of five counters while `tracesWithoutTotal` is a sum the API computes, so a state it
 * counts without breaking out would leave the reasons empty — the shortfall is still stated.
 * @param summary The window totals, or null when they could not be loaded.
 * @returns The sentence to show under the tiles, or null.
 */
export function costIncompletenessNote(summary: TraceSummary | null | undefined): string | null {
    if (!summary || summary.tracesWithoutTotal <= 0) return null;

    const counts = summary.costStatusCounts;
    const reasons = [
        counts?.unpriced ? `${counts.unpriced.toLocaleString('en-US')} nothing priced` : null,
        counts?.mixedCurrency
            ? `${counts.mixedCurrency.toLocaleString('en-US')} mixed currency`
            : null,
    ].filter((reason): reason is string => reason !== null);

    return (
        `${summary.tracesWithoutTotal.toLocaleString('en-US')} of ` +
        `${summary.traceCount.toLocaleString('en-US')} traces have no cost total` +
        `${reasons.length > 0 ? ` (${reasons.join(', ')})` : ''}, ` +
        'so the cost tiles are not the whole window.'
    );
}

/**
 * The "Data quality" note: metered spans whose instrumentation left out what cost is built from.
 *
 * A span with a model and no usage prices as "no tokens", and a span with no model cannot be
 * priced at all; either way it drops out of the cost tiles without a word, which is exactly how
 * Arize found 43% of one model's spans reporting zeros. Neither is our pricing's fault, so the note
 * points at the instrumentation rather than at Cost Configs.
 * @param summary The window totals, or null when they could not be loaded.
 * @returns The sentence to show beside the unpriced banner, or null when there is nothing to say.
 */
export function dataQualityNote(summary: TraceSummary | null | undefined): string | null {
    const zero = summary?.zeroTokenSpanCount ?? 0;
    const noModel = summary?.noModelSpanCount ?? 0;
    if (zero <= 0 && noModel <= 0) return null;

    const spans = (count: number) =>
        `${count.toLocaleString('en-US')} LLM ${count === 1 ? 'span' : 'spans'}`;
    const parts = [
        zero > 0 ? `${spans(zero)} reported no token usage` : null,
        noModel > 0
            ? `${zero > 0 ? noModel.toLocaleString('en-US') : spans(noModel)} named no model`
            : null,
    ].filter((part): part is string => part !== null);

    return `Data quality: ${parts.join(' and ')}. They add nothing to cost, so check the instrumentation behind them.`;
}

/** What to say when the trace list comes back with nothing in it. */
export interface TraceEmptyState {
    title: string;
    message: string;
    /** Whether a Clear filters button belongs here. False when there is nothing to clear. */
    canClearFilters: boolean;
}

/**
 * The words for an empty list, which are not the same words in the two cases it happens in.
 *
 * With a field filter set, the list is empty because of something the reader did, and the way out
 * is to undo it. With none set, it is empty because nothing has been traced in this window — a
 * project that has not sent anything yet lands here on its very first visit, and telling that
 * reader to "clear a filter" beside a button that clears nothing is a dead end at exactly the
 * moment the product should be welcoming them.
 * @param hasActiveFilters Whether any field filter — not the time range — is set.
 * @param rangeLabel The window in words, e.g. `the last 24 hours`.
 * @returns The heading, the sentence under it, and whether to offer Clear filters.
 */
export function traceEmptyState(hasActiveFilters: boolean, rangeLabel: string): TraceEmptyState {
    if (hasActiveFilters) {
        return {
            title: 'No traces match these filters',
            message:
                `Nothing in ${rangeLabel} matches every filter you have set. ` +
                'Clear one of them, or widen the time range.',
            canClearFilters: true,
        };
    }

    return {
        title: 'No traces yet',
        message:
            `Nothing has been traced in ${rangeLabel}. Point an OpenTelemetry exporter at this ` +
            'project and traces appear here within seconds of your pipeline running — every ' +
            'model call, retrieval and tool step, with its latency and its cost. If you expected ' +
            'one already, try a wider time range.',
        canClearFilters: false,
    };
}

/** One row of the flattened span tree the detail page renders. */
export interface SpanTreeRow {
    span: SpanSummary;
    /** Nesting level, 0 for a root. */
    depth: number;
    /** Whether any fetched span names this one as its parent, so the row can be collapsed. */
    hasChildren: boolean;
    /** Where this span starts, as a fraction of the trace's own window, 0–1. */
    offsetRatio: number;
    /** How wide this span's bar is, as a fraction of the trace's own window, 0–1. */
    widthRatio: number;
}

/**
 * The span of time a waterfall's bars are drawn against.
 *
 * Held as an explicit pair so the geometry can come from the trace header — which does not change
 * as pages of the tree arrive — rather than from whichever spans happen to have been fetched.
 */
export interface TraceWindow {
    startMs: number;
    endMs: number;
}

/**
 * A naive-UTC timestamp from the API as epoch milliseconds, or 0 when it cannot be read.
 * @param value The timestamp, with no `Z`.
 * @returns Milliseconds since the epoch.
 */
export function parseMs(value: string | null | undefined): number {
    // The parse is inlined against the same rules as `parseNaiveUtc` rather than imported, so this
    // module stays free of a dependency on the shared utils barrel.
    const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(
        value ?? '',
    );
    if (!match) {
        const parsed = new Date(value ?? '');

        return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }
    const [, year, month, day, hour, minute, second, fraction] = match;

    return Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        fraction ? Number(`${fraction}000`.slice(0, 3)) : 0,
    );
}

/**
 * The window a trace's own header declares, which is the one the bars should be drawn against.
 *
 * The header's `startedAt`/`endedAt` cover the whole trace and are the same on every page of the
 * tree, so geometry taken from them does not move as pages land. Null when either end is missing
 * or the pair is not a forward interval; the caller then falls back to the spans it holds.
 * @param detail The trace header, or null before it has arrived.
 * @returns The window, or null when the header does not describe one.
 */
export function traceWindow(
    detail: Pick<TraceItem, 'startedAt' | 'endedAt'> | null | undefined,
): TraceWindow | null {
    if (!detail) return null;
    const startMs = parseMs(detail.startedAt);
    const endMs = parseMs(detail.endedAt);
    if (!startMs || !endMs || endMs <= startMs) return null;

    return { startMs, endMs };
}

/**
 * The folded set with every ancestor of one span unfolded, so the tree shows that span.
 *
 * The agent graph can select a span inside a folded subtree; switching back to the tree would
 * otherwise leave the selection on no row and the inspector empty.
 * @param collapsed The ids of the folded spans.
 * @param spans Every span in hand.
 * @param spanId The span to reveal.
 * @returns `collapsed` itself when no ancestor was folded, else a new set without them.
 */
export function unfoldTo(
    collapsed: ReadonlySet<string>,
    spans: SpanSummary[],
    spanId: string,
): ReadonlySet<string> {
    const parentOf = new Map(spans.map(span => [span.spanId, span.parentSpanId]));
    const folded: string[] = [];
    const seen = new Set<string>([spanId]);
    let parent = parentOf.get(spanId) ?? null;
    while (parent !== null && !seen.has(parent)) {
        if (collapsed.has(parent)) folded.push(parent);
        seen.add(parent);
        parent = parentOf.get(parent) ?? null;
    }
    if (folded.length === 0) return collapsed;
    const next = new Set(collapsed);
    for (const id of folded) next.delete(id);

    return next;
}

/**
 * Flatten `spans` into render order: each root followed by its descendants, depth-first.
 *
 * The API already returns spans ordered by `(start_time, span_id)`, so the children of any parent
 * come out in start order. A span whose parent is missing — which happens whenever an earlier page
 * of the tree has not been fetched yet, and whenever a parent span has not been exported yet — is
 * treated as a root so it is still shown rather than silently dropped.
 * @param spans Every span fetched so far, across all pages of `GET /traces/{traceId}`.
 * @param window The trace header's own window. Pass it on a paged tree: without it the geometry is
 *   taken from the spans in hand, so every bar moves as each page arrives. Null falls back to the
 *   spans, which is right only when they are all of them.
 * @returns One row per span, in render order, with timeline geometry attached.
 */
export function buildSpanTree(
    spans: SpanSummary[],
    window: TraceWindow | null = null,
): SpanTreeRow[] {
    if (spans.length === 0) return [];

    const present = new Set(spans.map(span => span.spanId));
    const childrenOf = new Map<string, SpanSummary[]>();
    const roots: SpanSummary[] = [];
    // The extent of the spans in hand, folded into the pass that buckets them. It is only the
    // fallback for a `window` that was not supplied; taking it per call rather than spreading an
    // array of every start into `Math.min` is what keeps a very large trace from throwing
    // `RangeError` out of a `computed()` and blanking the tree.
    let earliest = Number.POSITIVE_INFINITY;
    let latest = Number.NEGATIVE_INFINITY;

    for (const span of spans) {
        const begin = parseMs(span.startTime);
        if (begin < earliest) earliest = begin;
        const finish = begin + Math.max(span.durationMs ?? 0, 0);
        if (finish > latest) latest = finish;

        const parentId = span.parentSpanId;
        if (parentId && present.has(parentId) && parentId !== span.spanId) {
            const siblings = childrenOf.get(parentId);
            if (siblings) siblings.push(span);
            else childrenOf.set(parentId, [span]);
        } else {
            roots.push(span);
        }
    }

    const windowStart = window ? window.startMs : earliest;
    const windowEnd = window ? window.endMs : latest;
    const windowWidth = Math.max(windowEnd - windowStart, 1);

    const rows: SpanTreeRow[] = [];
    // Walked with an explicit stack rather than by recursion: a trace is a shape the exporter
    // chose, and a chain tens of thousands of spans deep would overflow the call stack. Children
    // are pushed in reverse so they come back off in the order the API sent them.
    const stack: { span: SpanSummary; depth: number }[] = [];
    for (let index = roots.length - 1; index >= 0; index -= 1) {
        stack.push({ span: roots[index], depth: 0 });
    }

    while (stack.length > 0) {
        const { span, depth } = stack.pop();
        const begin = parseMs(span.startTime);
        // Clamped to the window. A span can sit outside the header's interval when the exporter's
        // clock and the collector's disagree; widening the window to swallow it would bring back
        // the very rescaling this window exists to stop, so the bar is pinned to the edge instead.
        const offsetRatio = Math.min(Math.max((begin - windowStart) / windowWidth, 0), 1);
        rows.push({
            span,
            depth,
            hasChildren: childrenOf.has(span.spanId),
            offsetRatio,
            widthRatio: Math.max(
                Math.min((span.durationMs ?? 0) / windowWidth, 1 - offsetRatio),
                0.004,
            ),
        });

        const children = childrenOf.get(span.spanId);
        if (!children) continue;
        for (let index = children.length - 1; index >= 0; index -= 1) {
            stack.push({ span: children[index], depth: depth + 1 });
        }
    }

    return rows;
}

/**
 * The route to a tracing page, for whichever mount the user is on.
 *
 * The monitor module is mounted twice: under `/:projectSlug/monitor/...` and, for a user who has
 * not picked a project, at `/monitor/...`. A link built with an empty slug segment matches neither,
 * and the app's catch-all then sends the reader to the home page -- which is what clicking a trace
 * used to do outside a project.
 * @param projectSlug The slug from the route, absent on the project-less mount.
 * @param rest The segments after `monitor`, for example the tracing path and a trace id.
 * @returns Router commands for the current mount.
 */
/**
 * A route to one object of a project-scoped module, honouring the mount the reader is on.
 *
 * The same rule `tracingLink` keeps: the tracing pages are mounted both with and without a project
 * slug, and a link built for the wrong mount matches no route and falls through to the home
 * redirect.
 * @param projectSlug The slug from the current route, if the reader is inside a project.
 * @param modulePath The module segment, for example `management`.
 * @param pagePath The page segment, for example `deployments`.
 * @param id The object's id.
 * @returns The router link array.
 */
export function objectLink(
    projectSlug: string | null | undefined,
    modulePath: string,
    pagePath: string,
    id: number,
): string[] {
    const segments = [modulePath, pagePath, String(id)];

    return projectSlug ? ['/', projectSlug, ...segments] : ['/', ...segments];
}

/** The registry page of each kind of asset a pipeline declares. */
const ASSET_PAGES: Partial<Record<TraceAsset['kind'], PAGES>> = {
    rag: PAGES.RAG_REGISTRY,
    prompt: PAGES.PROMPT_REGISTRY,
    foundation_model: PAGES.MODEL_CATALOG,
};

/**
 * The page of the pipeline version a trace ran.
 *
 * Registry pages are keyed by the version's id -- the page shows that version and lists the
 * others -- so the link carries `pipelineVersionId`, never the pipeline's own id, which would
 * open a different object or none.
 * @param projectSlug The slug from the current route, if the reader is inside a project.
 * @param pipelineVersionId The id from the trace's origin block.
 * @returns The router link array.
 */
export function pipelineVersionLink(
    projectSlug: string | null | undefined,
    pipelineVersionId: number,
): string[] {
    return objectLink(
        projectSlug,
        MODULE_ROUTE_MAP[MODULES.ASSETS],
        PAGE_ROUTE_MAP[PAGES.PIPELINE_BUILDER],
        pipelineVersionId,
    );
}

/**
 * The page of one of the pipeline's declared assets, keyed by the declared version's id.
 *
 * An unknown kind is possible: the API passes `kind` through rather than mapping it onto a closed
 * set, so a kind added on the API side reaches here. It renders without a link instead of being
 * dropped or pointed at the wrong registry.
 * @param projectSlug The slug from the current route, if the reader is inside a project.
 * @param asset The asset from the trace's origin block.
 * @returns The router link array, or null when this build does not know the kind.
 */
export function traceAssetLink(
    projectSlug: string | null | undefined,
    asset: TraceAsset,
): string[] | null {
    const page = ASSET_PAGES[asset.kind];

    return page === undefined
        ? null
        : objectLink(
              projectSlug,
              MODULE_ROUTE_MAP[MODULES.ASSETS],
              PAGE_ROUTE_MAP[page] ?? '',
              asset.versionId,
          );
}

export function tracingLink(projectSlug: string | null | undefined, ...rest: string[]): string[] {
    return projectSlug ? ['/', projectSlug, 'monitor', ...rest] : ['/', 'monitor', ...rest];
}

/**
 * One conversation as a transcript: its rollup, and every turn oldest first.
 *
 * A turn is a trace, carried with the header's full previews rather than the list's cut, so the
 * page shows what was asked and answered without reading a span per turn.
 */
export interface SessionDetail {
    session: SessionItem;
    turns: TraceItem[];
    /** True when the API cut the turns at its page cap. `session.traceCount` is the real total. */
    turnsTruncated: boolean;
}

/**
 * One session: the traces that share a `sessionId`, rolled up.
 *
 * Sessions are derived from the trace headers, not stored, so a session exists exactly as long as
 * its traces do. Its traces come from `GET /traces?sessionId=...`, and each trace's spans from the
 * trace detail — session → traces → spans.
 */
export interface SessionItem {
    sessionId: string;
    /** One per turn of the conversation. */
    traceCount: number;
    startedAt: string;
    endedAt: string;
    /** Wall-clock from the first trace starting to the last one ending. */
    durationMs: number;
    errorCount: number;
    inputTokens: number;
    outputTokens: number;
    /** One entry per currency, never a single total: micros in different currencies do not add up. */
    costByCurrency: CurrencyTotal[];
    /** Traces here with no cost at all, so a small total is not read as a cheap session. */
    unpricedTraceCount: number;
    userIds: string[];
    /** The first turn's input and the last turn's output, masked and cut to the list's length. */
    firstMessage?: string | null;
    lastMessage?: string | null;
    /** Tool and agent spans over every turn. */
    toolCallCount?: number;
    agentCount?: number;
    /** Turns carrying any trajectory flag. */
    flaggedTraceCount?: number;
    /** One rollup per score name: the session's own scores and its turns' together. */
    scores?: ScoreRollup[];
    /** The readable projects its traces belong to; one in practice. */
    projectIds: number[];
}

/**
 * The project a session belongs to, or null when its traces are in several (or none readable):
 * a session id is the application's, and a guess would run a judge in the wrong project.
 * @param session The session rollup.
 * @returns The project id, or null.
 */
export function sessionProjectId(session: Pick<SessionItem, 'projectIds'>): number | null {
    return session.projectIds.length === 1 ? session.projectIds[0] : null;
}
